import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { Keepalive } from '@ng-idle/keepalive';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { SessionService } from './services/session.service';
import { Meta, Title } from '@angular/platform-browser';
import { ClientBrowserSessionStorageService } from './services/client-browser-session-storage.service';
import { Commons } from './shared/Commons';
import { filter } from 'rxjs';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { TranslocoService } from '@ngneat/transloco';
import { CookieConsentBannerComponent } from './shared/components/cookie-consent-banner/cookie-consent-banner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CookieConsentBannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = environment.dfConfigCompanyName;
  KEEPALIVE_TIME: number = 3
  SESSION_TIME_MINS: number = environment.sessionTimeMins
  IDLE_TIME: number = (this.SESSION_TIME_MINS * 60)
  showChatBot = environment.chat_button.enabled && environment.chat_button.type === 'chat_bot'
  isChatbotOpen = false;

  constructor(
    // 👇 Se declara GoogleAnalyticsService en el constructor para inyectar script
    private readonly ga: GoogleAnalyticsService,
    private idle: Idle,
    keepalive: Keepalive,
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private titleService: Title,
    private metaService: Meta,
    private sessionStorageService: ClientBrowserSessionStorageService,
    private translocoService: TranslocoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (Commons.isBrowser(platformId)) {
      keepalive.interval(this.KEEPALIVE_TIME);
      keepalive.onPing.subscribe(() => {
        if (Commons.isGAuthCredentials(sessionStorageService)) {
          const diffMins = Math.floor((Date.now() - Commons.sessionObject(sessionStorageService).time) / 60000);
          if (diffMins > this.SESSION_TIME_MINS) {
            //Force close session because google token expires in 1 hour
            this.closeSessionExpToken()
          }
        } else {
          Commons.sessionRenew(sessionStorageService)
        }
      });

      idle.setIdle(this.IDLE_TIME); // Define el tiempo de inactividad en segundos.
      idle.setTimeout(1); // Define el tiempo de espera en segundos antes de que se dispare el evento timeout.
      idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      idle.onIdleEnd.subscribe(() => { });
      idle.onTimeout.subscribe(() => {
        this.closeSession()
      });
      idle.onIdleStart.subscribe(() => { });
      idle.onTimeoutWarning.subscribe((countdown) => { });

      this.sessionService.getUserLoggedIn().subscribe(userLoggedIn => {
        if (userLoggedIn) {
          idle.watch();
        } else {
          idle.stop();
        }
      })
    }
  }

  reset() {
    this.idle.watch();
  }

  ngOnInit(): void {
    this.translocoService.setActiveLang(environment.dfConfigLang);
    if (this.isBrowser) {
      this.loadMetaTags()
      this.reset();
    }
  }

  get isBrowser() {
    return Commons.isBrowser(this.platformId);
  }

  closeSession() {
    if (Commons.sessionIsOpen(this.sessionStorageService)) {
      this.sessionService.setUserLoggedIn(false);
      Commons.sessionClose(this.sessionStorageService)
      this.router.navigate([Commons.PATH_LOGIN], { queryParams: { arg: 'closed' } })
    }
  }

  closeSessionExpToken() {
    if (Commons.sessionIsOpen(this.sessionStorageService)) {
      this.sessionService.setUserLoggedIn(false);
      Commons.sessionClose(this.sessionStorageService)
      this.router.navigate([Commons.PATH_LOGIN], { queryParams: { arg: 'expToken' } })
    }
  }

  loadMetaTags() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    )
      .subscribe(() => {

        let rt = this.getChild(this.route)

        rt.data.subscribe((data: any) => {
          this.titleService.setTitle(data.title)

          if (data.description) {
            this.metaService.updateTag({ name: 'description', content: data.description })
          } else {
            this.metaService.removeTag("name='description'")
          }

          if (data.robots) {
            this.metaService.updateTag({ name: 'robots', content: data.robots })
          } else {
            this.metaService.updateTag({ name: 'robots', content: "follow,index" })
          }

          if (data.ogUrl) {
            this.metaService.updateTag({ property: 'og:url', content: data.ogUrl })
          } else {
            this.metaService.updateTag({ property: 'og:url', content: this.router.url })
          }

          if (data.ogTitle) {
            this.metaService.updateTag({ property: 'og:title', content: data.ogTitle })
          } else {
            this.metaService.removeTag("property='og:title'")
          }

          if (data.ogDescription) {
            this.metaService.updateTag({ property: 'og:description', content: data.ogDescription })
          } else {
            this.metaService.removeTag("property='og:description'")
          }

          if (data.ogImage) {
            this.metaService.updateTag({ property: 'og:image', content: data.ogImage })
          } else {
            this.metaService.removeTag("property='og:image'")
          }


          if (data.content) {
            this.metaService.updateTag({ content: data.content, name: 'keywords' })
          } else {
            this.metaService.removeTag("content")
          }

        })

      })

  }

  getChild(activatedRoute: ActivatedRoute): any {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }

  }

  openChatbot() {
    this.isChatbotOpen = true;
  }

  closeChatbot() {
    this.isChatbotOpen = false;
  }
}
